// RichTextRenderer.jsx

import React, { useEffect, useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { normalizeRichTextValue, serializeRichTextDocument, trimTrailingEmptyRichTextBlocks } from 'utilities/RichText';

// CSS
import './RichTextEditor.css';
import './RichTextRenderer.css';

// Components
import { createRichTextExtensions } from './richTextTiptap';
import { EditorContent, useEditor } from '@tiptap/react';

/**
 * RichTextRenderer
 *
 * Renders normalized rich text content in read-only mode.
 *
 * @param {boolean} compact - Whether to use the compact content sizing.
 * @param {object|null} value - Current normalized or raw rich text value.
 *
 * @returns {ReactNode} The rendered rich text content.
 */
const RichTextRenderer = React.memo(({
    compact = false,
    value,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- MEMOS ----- //

    const normalizedValue = useMemo(() => {
        const nextValue = normalizeRichTextValue(value);

        return {
            ...nextValue,
            content: trimTrailingEmptyRichTextBlocks(nextValue.content),
        };
    }, [value]);
    const serializedContent = useMemo(() => serializeRichTextDocument(normalizedValue.content), [normalizedValue.content]);
    const compactClassName = compact ? ' is-compact' : '';

    // ----- HOOKS ----- //

    const editor = useEditor({
        content: normalizedValue.content,
        editable: false,
        editorProps: {
            attributes: {
                class: `rich-text-editor-content rich-text-renderer-content${compactClassName}`,
                tabindex: '-1',
            },
        },
        extensions: createRichTextExtensions({ readOnly: true }),
        immediatelyRender: false,
    }, [compactClassName]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!editor) return;

        const currentSerialized = serializeRichTextDocument(editor.getJSON());
        if (currentSerialized === serializedContent) return;

        editor.commands.setContent(normalizedValue.content, false);
    }, [editor, normalizedValue.content, serializedContent]);

    // ----- RENDER ----- //

    return (
        <div
          className="rich-text-renderer-container"
          style={{
            '--rich-text-selected-border': theme.accent,
          }}
        >
            {/* SURFACE */}
            <div
              className={`rich-text-renderer-surface${compactClassName}`}
            >
                <EditorContent editor={editor} />
            </div>
        </div>
    );
});

// Dev Tools
RichTextRenderer.displayName = 'RichTextRenderer';

export default RichTextRenderer;
